import { BlockBlobClient } from '@azure/storage-blob';
import http from '../../../client';

type MediaType =
  | 'cover'
  | 'gallery'
  | 'cover-video'
  | 'preparation-video'
  | 'step-image'
  | 'step-video';

type SasResponse = {
  uploadUrl: string;
  blobUrl: string;
  blobName: string;
  expiresInMinutes: number;
};

const MAX_RECIPE_VIDEO_SIZE_BYTES = 200 * 1024 * 1024;

/** Browser block upload parallelism (lower = fewer concurrent chunk requests). */
const UPLOAD_CONCURRENCY = 3;

/** Full-upload retries after transient failures (each attempt re-runs staged blocks). */
const MAX_UPLOAD_ATTEMPTS = 4;

/** SAS lifetime requested from the API (backend clamps to a safe range). */
export const RECIPE_UPLOAD_SAS_EXPIRY_MINUTES = 25;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function isRetryableUploadError(err: unknown): boolean {
  if (err == null) return false;
  if (typeof err !== 'object') return true;
  const o = err as { code?: string; statusCode?: number; name?: string };
  if (o.name === 'AbortError' || o.code === 'AbortError') return false;
  const s = o.statusCode;
  if (s === 403 || s === 400 || s === 404 || s === 401) return false;
  if (s !== undefined && s < 500 && s !== 408 && s !== 429) return false;
  return true;
}

async function uploadBrowserDataWithRetry(
  blockBlobClient: BlockBlobClient,
  file: File,
  uploadOptions: Parameters<BlockBlobClient['uploadBrowserData']>[1],
): Promise<void> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
    try {
      await blockBlobClient.uploadBrowserData(file, uploadOptions);
      return;
    } catch (e) {
      lastErr = e;
      if (!isRetryableUploadError(e) || attempt === MAX_UPLOAD_ATTEMPTS) {
        throw e;
      }
      await sleep(Math.min(8000, 400 * 2 ** (attempt - 1)));
    }
  }
  throw lastErr;
}

export const uploadFileDirectToAzure = async (
  file: File,
  options: {
    mediaType: MediaType;
    recipeId?: string | number | null;
    onProgress?: (percent: number) => void;
  },
): Promise<string> => {
  const isRecipeVideo =
    options.mediaType === 'cover-video' ||
    options.mediaType === 'preparation-video';
  if (isRecipeVideo && file.size > MAX_RECIPE_VIDEO_SIZE_BYTES) {
    throw new Error(
      `Video size must be less than 200MB. Selected file is ${(
        file.size /
        (1024 * 1024)
      ).toFixed(1)}MB.`,
    );
  }

  const sasRes = await http.post('/recipes/generate-sas', {
    fileName: file.name,
    mediaType: options.mediaType,
    recipeId: options.recipeId ?? undefined,
    expiresInMinutes: RECIPE_UPLOAD_SAS_EXPIRY_MINUTES,
  });

  const data = (
    sasRes.data && typeof sasRes.data === 'object' && 'data' in sasRes.data
      ? (sasRes.data as { data: SasResponse }).data
      : sasRes.data
  ) as SasResponse;

  const blockBlobClient = new BlockBlobClient(data.uploadUrl);
  await uploadBrowserDataWithRetry(blockBlobClient, file, {
    blockSize: 4 * 1024 * 1024,
    concurrency: UPLOAD_CONCURRENCY,
    maxSingleShotSize: 8 * 1024 * 1024,
    onProgress: (ev) => {
      if (!options.onProgress) return;
      const percent =
        file.size > 0 ? Math.min(100, (ev.loadedBytes / file.size) * 100) : 100;
      options.onProgress(percent);
    },
  });

  return data.blobUrl;
};
